import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Repeat } from 'lucide-react';
import { DatePicker } from '@/components/ui';
import { RECURRENCE_COPY } from '@/constants/recurrenceCopy';
import {
  parseRecurrence,
  summarizeRecurrence,
  untilErrorMessage,
  validateUntil,
  type UntilError,
} from '@/lib/recurrence';
import { toDateKeyTz } from '@/lib/tz';
import { isMemoryFilled, MEMORY_FILL_SUFFIX } from '@/lib/memoryFill';
import type { ReviewItemWithChild } from '@/types';

export interface RecurrenceSummaryProps {
  item: ReviewItemWithChild;
  timezone?: string | null;
  /**
   * Persist a new end date (`null` clears it). Omit for a read-only card —
   * the summary still shows, the end-date control does not. Rejects on a
   * failed save; the component shows the error inline.
   */
  onChangeUntil?: (
    item: ReviewItemWithChild,
    until: string | null,
  ) => Promise<void>;
}

// The review card is itself a button that opens the edit form; nothing
// inside this block may bubble up to it.
const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation();

/**
 * The recurring-event block on a Review event card (YAR-443). Renders nothing
 * for an item without a usable rule or without a start date, so every other
 * card is untouched.
 *
 *   no end date: "Repeats every Tuesday" + the first-session-only hint
 *   end date:    "Every Tuesday · through Dec 18 · 12 sessions"
 *   filled end:  "Every Tuesday · through Dec 18 · 12 sessions — from your
 *                 past edits" (Phase 3b: an end Yaruta filled from memory)
 *
 * The end date is the only editable part of the rule.
 */
export function RecurrenceSummary({
  item,
  timezone,
  onChangeUntil,
}: RecurrenceSummaryProps) {
  const [validation, setValidation] = useState<UntilError | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const rule = parseRecurrence(item.recurrence);
  if (!rule || !item.start_datetime) return null;
  const startKey = toDateKeyTz(item.start_datetime, timezone);
  const summary = summarizeRecurrence(rule, startKey);
  const untilFilled = isMemoryFilled(
    item.raw_extraction,
    'recurrence_until',
    rule.until,
  );

  const save = async (until: string | null) => {
    if (!onChangeUntil || saving) return;
    setSaving(true);
    setSaveFailed(false);
    try {
      await onChangeUntil(item, until);
    } catch {
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const handlePick = (until: string) => {
    const error = validateUntil(until, startKey);
    setValidation(error);
    if (error) return;
    void save(until);
  };

  const handleClear = () => {
    setValidation(null);
    void save(null);
  };

  const message = validation
    ? untilErrorMessage(validation)
    : saveFailed
      ? RECURRENCE_COPY.saveError
      : null;

  return (
    // Laid out as one more row of the card's date / time / location list:
    // same 20px muted icon and text-sm, and everything under the first line
    // indented to the text column (20px icon + 6px gap). It used to be a
    // padded panel of its own, which in the narrow card column pushed the
    // icon, the hint and the picker out of line with the rows above.
    <div
      className="flex flex-col gap-2"
      onClick={stop}
      onKeyDown={stop}
      data-testid="recurrence-summary"
    >
      <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
        <Repeat
          size={20}
          className="flex-shrink-0"
          aria-hidden="true"
        />
        <span>
          {summary.line}
          {untilFilled && ` — ${MEMORY_FILL_SUFFIX}`}
        </span>
      </span>
      <div className="flex flex-col gap-2 pl-[26px] empty:hidden">
        {summary.kind === 'open' && (
          <span className="text-xs text-muted-foreground">{summary.hint}</span>
        )}
        {onChangeUntil && (
          <DatePicker
            value={rule.until ?? ''}
            onChange={handlePick}
            placeholder={RECURRENCE_COPY.endDatePlaceholder}
            error={!!validation}
            // Open on the first session's month, not today's.
            defaultMonth={startKey}
            // The Review card is overflow-hidden: float the calendar above it.
            floating
          />
        )}
        {onChangeUntil && (saving || rule.until) && (
          <div className="flex items-center justify-between gap-2">
            {saving ? (
              <span
                className="text-xs text-muted-foreground"
                role="status"
              >
                {RECURRENCE_COPY.saving}
              </span>
            ) : (
              <span />
            )}
            {rule.until && (
              <button
                type="button"
                onClick={handleClear}
                disabled={saving}
                aria-label={RECURRENCE_COPY.clearEndDateAria}
                className="text-sm font-medium text-muted-foreground hover:text-foreground cursor-pointer disabled:cursor-default disabled:opacity-50"
              >
                {RECURRENCE_COPY.clearEndDate}
              </button>
            )}
          </div>
        )}
        {message && (
          <span
            role="alert"
            className="text-sm text-text-error"
          >
            {message}
          </span>
        )}
      </div>
    </div>
  );
}
